/**
 * ==============================================================================
 * GULCAN HEADER JAVASCRIPT
 * ==============================================================================
 * Handles all header interactions:
 * - Mobile menu open/close
 * - Search bar toggle
 * - Subcategory navigation (drill-down)
 * - Body scroll lock
 * - Wishlist count (Swish integration)
 * - Cart count updates
 * ==============================================================================
 */

(function() {
  'use strict';

  // ==============================================================================
  // DOM ELEMENTS
  // ==============================================================================

  var elements = {
    // Menu
    menuTrigger: document.getElementById('gulcan-menu-trigger'),
    mobileMenu: document.getElementById('gulcan-mobile-menu'),
    menuOverlay: document.getElementById('gulcan-menu-overlay'),
    menuClose: document.getElementById('gulcan-menu-close'),
    menuContent: document.getElementById('gulcan-menu-content'),
    menuMain: document.getElementById('gulcan-menu-main'),

    // Search
    searchTrigger: document.getElementById('gulcan-search-trigger'),
    searchBar: document.getElementById('gulcan-search-bar'),
    searchInput: document.getElementById('gulcan-search-input'),
    searchClose: document.getElementById('gulcan-search-close'),

    // Cart & Wishlist
    cartTrigger: document.getElementById('gulcan-cart-icon'),
    cartCount: document.getElementById('gulcan-cart-count'),
    wishlistCount: document.getElementById('gulcan-wishlist-count')
  };

  // State
  var state = {
    menuOpen: false,
    searchOpen: false,
    currentSubmenu: null,
    scrollPosition: 0
  };

  // ==============================================================================
  // MENU FUNCTIONS
  // ==============================================================================

  /**
   * Opens the mobile menu drawer
   */
  function openMenu() {
    if (state.menuOpen) return;

    state.menuOpen = true;
    state.scrollPosition = window.scrollY;

    // Add classes
    document.body.classList.add('gulcan-menu-open');
    elements.mobileMenu.classList.add('is-open');

    // Update aria attributes
    if (elements.menuTrigger) {
      elements.menuTrigger.setAttribute('aria-expanded', 'true');
    }

    // Focus on close button for accessibility
    if (elements.menuClose) {
      setTimeout(function() {
        elements.menuClose.focus();
      }, 100);
    }
  }

  /**
   * Closes the mobile menu drawer
   */
  function closeMenu() {
    if (!state.menuOpen) return;

    state.menuOpen = false;

    // Remove classes
    document.body.classList.remove('gulcan-menu-open');
    elements.mobileMenu.classList.remove('is-open');

    // Update aria attributes
    if (elements.menuTrigger) {
      elements.menuTrigger.setAttribute('aria-expanded', 'false');
    }

    // Close any open submenus
    closeAllSubmenus();

    // Restore scroll position
    window.scrollTo(0, state.scrollPosition);

    // Return focus to menu trigger
    if (elements.menuTrigger) {
      elements.menuTrigger.focus();
    }
  }

  /**
   * Toggles the mobile menu
   */
  function toggleMenu() {
    if (state.menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }

  // ==============================================================================
  // SUBMENU FUNCTIONS (Drill-down navigation)
  // ==============================================================================

  /**
   * Opens a submenu panel
   * @param {number|string} submenuId - The submenu identifier
   */
  function openSubmenu(submenuId) {
    var submenuPanel = document.querySelector('[data-submenu-panel="' + submenuId + '"]');

    if (!submenuPanel) return;

    state.currentSubmenu = submenuId;

    // Activate the submenu panel
    submenuPanel.classList.add('is-active');

    // Update aria on trigger button
    var trigger = document.querySelector('[data-submenu="' + submenuId + '"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Hide main menu content
    if (elements.menuMain) {
      elements.menuMain.style.display = 'none';
    }
  }

  /**
   * Closes the current submenu and returns to main menu
   */
  function closeSubmenu() {
    if (!state.currentSubmenu) return;

    var submenuPanel = document.querySelector('[data-submenu-panel="' + state.currentSubmenu + '"]');

    if (submenuPanel) {
      submenuPanel.classList.remove('is-active');
    }

    // Update aria on trigger button
    var trigger = document.querySelector('[data-submenu="' + state.currentSubmenu + '"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    // Show main menu content again
    if (elements.menuMain) {
      elements.menuMain.style.display = '';
    }

    state.currentSubmenu = null;
  }

  /**
   * Closes all submenus
   */
  function closeAllSubmenus() {
    var allSubmenus = document.querySelectorAll('.gulcan-mobile-menu__subcategory.is-active');
    allSubmenus.forEach(function(panel) {
      panel.classList.remove('is-active');
    });

    var allTriggers = document.querySelectorAll('.gulcan-menu-has-children');
    allTriggers.forEach(function(trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    });

    if (elements.menuMain) {
      elements.menuMain.style.display = '';
    }

    state.currentSubmenu = null;
  }

  // ==============================================================================
  // SEARCH FUNCTIONS
  // ==============================================================================

  /**
   * Opens the search bar
   */
  function openSearch() {
    if (state.searchOpen) return;

    state.searchOpen = true;

    if (elements.searchBar) {
      elements.searchBar.classList.add('is-open');
    }

    if (elements.searchTrigger) {
      elements.searchTrigger.setAttribute('aria-expanded', 'true');
    }

    // Focus on search input
    if (elements.searchInput) {
      setTimeout(function() {
        elements.searchInput.focus();
      }, 100);
    }
  }

  /**
   * Closes the search bar
   */
  function closeSearch() {
    if (!state.searchOpen) return;

    state.searchOpen = false;

    if (elements.searchBar) {
      elements.searchBar.classList.remove('is-open');
    }

    if (elements.searchTrigger) {
      elements.searchTrigger.setAttribute('aria-expanded', 'false');
    }

    // Clear input
    if (elements.searchInput) {
      elements.searchInput.value = '';
    }
  }

  /**
   * Toggles the search bar
   */
  function toggleSearch() {
    if (state.searchOpen) {
      closeSearch();
    } else {
      openSearch();
    }
  }

  // ==============================================================================
  // CART COUNT FUNCTIONS
  // ==============================================================================

  /**
   * Updates the cart count badge
   * @param {number} count - The cart item count
   */
  function updateCartCount(count) {
    if (!elements.cartCount) return;

    elements.cartCount.textContent = count;
    elements.cartCount.setAttribute('data-count', count);

    if (count > 0) {
      elements.cartCount.style.display = '';
    } else {
      elements.cartCount.style.display = 'none';
    }
  }

  /**
   * Fetches the current cart and updates the count
   */
  function refreshCartCount() {
    fetch('/cart.js')
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        updateCartCount(cart.item_count);
      })
      .catch(function(error) {
        console.warn('Gulcan Header: Could not fetch cart', error);
      });
  }

  // ==============================================================================
  // WISHLIST COUNT FUNCTIONS (Swish / Wishlist King integration)
  // ==============================================================================

  /**
   * Updates the wishlist count badge
   * @param {number} count - The wishlist item count
   */
  function updateWishlistCount(count) {
    if (!elements.wishlistCount) return;

    elements.wishlistCount.textContent = count;

    if (count > 0) {
      elements.wishlistCount.style.display = '';
    } else {
      elements.wishlistCount.style.display = 'none';
    }
  }

  /**
   * Gets wishlist count from Swish (Wishlist King) app
   * Tracks product handles in localStorage for persistence across pages
   *
   * Supports multiple Swish button types:
   * - wishlist-button-collection: Used on collection/grid pages (has data-product-handle)
   * - wishlist-button: Generic wishlist button (has data-product-handle)
   * - wk-button: Used on product detail pages (NO data-product-handle, get from URL)
   */
  function refreshWishlistCount() {
    var STORAGE_KEY = 'gulcan_wishlist_items';

    // Get wishlist items from localStorage
    function getStoredItems() {
      try {
        var stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        return [];
      }
    }

    // Save wishlist items to localStorage
    function saveItems(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {}
    }

    // Get current product handle from URL (for product pages)
    function getProductHandleFromURL() {
      var match = window.location.pathname.match(/\/products\/([^?/]+)/);
      return match ? match[1] : null;
    }

    // Check if we're on a product page
    function isProductPage() {
      return window.location.pathname.indexOf('/products/') !== -1;
    }

    // Sync localStorage with what's visible on the current page
    function syncWithPage() {
      var storedItems = getStoredItems();

      // Check collection page buttons (wishlist-button-collection, wishlist-button)
      var selectedContainers = document.querySelectorAll(
        'wishlist-button-collection:has(.wk-selected), ' +
        'wishlist-button:has(.wk-selected)'
      );

      // Get handles from selected buttons on this page
      var pageHandles = [];
      selectedContainers.forEach(function(container) {
        var handle = container.dataset.productHandle;
        if (handle && pageHandles.indexOf(handle) === -1) {
          pageHandles.push(handle);
        }
      });

      // Also check PDP wk-button (floating button on product pages)
      if (isProductPage()) {
        var wkButton = document.querySelector('wk-button');
        if (wkButton) {
          var isSelected = wkButton.querySelector('.wk-selected') !== null;
          var pdpHandle = getProductHandleFromURL();
          if (pdpHandle) {
            if (isSelected && pageHandles.indexOf(pdpHandle) === -1) {
              pageHandles.push(pdpHandle);
            }
          }
        }
      }

      // Add any selected items from this page that aren't in storage
      pageHandles.forEach(function(handle) {
        if (storedItems.indexOf(handle) === -1) {
          storedItems.push(handle);
        }
      });

      // Get all button handles on this page (to check for removals)
      var allContainers = document.querySelectorAll(
        'wishlist-button-collection[data-product-handle], ' +
        'wishlist-button[data-product-handle]'
      );
      var allPageHandles = [];
      allContainers.forEach(function(container) {
        var handle = container.dataset.productHandle;
        if (handle && allPageHandles.indexOf(handle) === -1) {
          allPageHandles.push(handle);
        }
      });

      // Add PDP handle to allPageHandles if on product page
      if (isProductPage()) {
        var pdpHandle = getProductHandleFromURL();
        if (pdpHandle && allPageHandles.indexOf(pdpHandle) === -1) {
          allPageHandles.push(pdpHandle);
        }
      }

      // Remove items that are on this page but NOT selected (user removed them)
      storedItems = storedItems.filter(function(handle) {
        // Keep if not on this page OR if it's selected
        var isOnPage = allPageHandles.indexOf(handle) !== -1;
        var isSelected = pageHandles.indexOf(handle) !== -1;
        return !isOnPage || isSelected;
      });

      saveItems(storedItems);
      updateWishlistCount(storedItems.length);
    }

    // Handle wishlist button click (collection pages)
    function handleButtonClick(container) {
      var handle = container.dataset.productHandle;
      if (!handle) return;

      // Wait for Swish to update the button state
      setTimeout(function() {
        var isSelected = container.querySelector('.wk-selected') !== null;
        var storedItems = getStoredItems();
        var index = storedItems.indexOf(handle);

        if (isSelected && index === -1) {
          // Add to wishlist
          storedItems.push(handle);
        } else if (!isSelected && index !== -1) {
          // Remove from wishlist
          storedItems.splice(index, 1);
        }

        saveItems(storedItems);
        updateWishlistCount(storedItems.length);
      }, 300);
    }

    // Handle PDP wk-button click (product detail pages)
    function handlePDPButtonClick(wkButton) {
      var handle = getProductHandleFromURL();
      if (!handle) return;

      // Wait for Swish to update the button state
      setTimeout(function() {
        var isSelected = wkButton.querySelector('.wk-selected') !== null;
        var storedItems = getStoredItems();
        var index = storedItems.indexOf(handle);

        if (isSelected && index === -1) {
          // Add to wishlist
          storedItems.push(handle);
        } else if (!isSelected && index !== -1) {
          // Remove from wishlist
          storedItems.splice(index, 1);
        }

        saveItems(storedItems);
        updateWishlistCount(storedItems.length);
      }, 300);
    }

    // Setup click listener
    // Use capture phase (true) to catch clicks before they're stopped by other handlers
    function setupClickListener() {
      document.addEventListener('click', function(e) {
        // Handle collection page buttons (wishlist-button-collection, wishlist-button)
        var container = e.target.closest('wishlist-button-collection') ||
                        e.target.closest('wishlist-button');
        if (container) {
          handleButtonClick(container);
          return;
        }

        // Handle PDP floating button (wk-button)
        // Check for wk-button OR elements inside it (button, svg, path)
        var wkButton = e.target.closest('wk-button');
        if (wkButton) {
          handlePDPButtonClick(wkButton);
          return;
        }

        // Handle remove button on wishlist page (/apps/wishlist)
        var removeBtn = e.target.closest('remove-button');
        if (removeBtn) {
          // Find the product card to get the handle
          var card = removeBtn.closest('wishlist-product-card');
          if (card) {
            // Get product handle from the link
            var link = card.querySelector('a[href*="/products/"]');
            if (link) {
              var href = link.getAttribute('href');
              var match = href.match(/\/products\/([^?/]+)/);
              if (match) {
                var handle = match[1];
                var storedItems = getStoredItems();
                var index = storedItems.indexOf(handle);
                if (index !== -1) {
                  storedItems.splice(index, 1);
                  saveItems(storedItems);
                  updateWishlistCount(storedItems.length);
                }
              }
            }
          }
        }
      }, true); // Use capture phase to catch events early
    }

    // Check if we're on the wishlist page
    function isWishlistPage() {
      return window.location.pathname.indexOf('/apps/wishlist') !== -1;
    }

    /**
     * Sync localStorage with actual wishlist page contents.
     * This is a "security safety" - the wishlist page is the source of truth.
     * If there's any drift (e.g., user removed items from another device),
     * this will correct the header count.
     */
    function syncWithWishlistPage() {
      // Find all wishlist product cards on the page
      var productCards = document.querySelectorAll('wishlist-product-card');
      var actualHandles = [];

      productCards.forEach(function(card) {
        // Get product handle from the product link
        var link = card.querySelector('a[href*="/products/"]');
        if (link) {
          var href = link.getAttribute('href');
          var match = href.match(/\/products\/([^?/]+)/);
          if (match && actualHandles.indexOf(match[1]) === -1) {
            actualHandles.push(match[1]);
          }
        }
      });

      // Replace localStorage with actual wishlist contents
      saveItems(actualHandles);
      updateWishlistCount(actualHandles.length);

      console.log('[Gulcan Wishlist] Synced with wishlist page:', actualHandles.length, 'items');
    }

    // Initialize
    function init() {
      // Show stored count immediately
      updateWishlistCount(getStoredItems().length);

      // Setup click listener right away
      setupClickListener();

      // Check if on wishlist page - sync with actual contents (source of truth)
      if (isWishlistPage()) {
        // Wait for wishlist page to load its products
        var wishlistCheckCount = 0;
        var wishlistCheckInterval = setInterval(function() {
          wishlistCheckCount++;
          var hasCards = document.querySelector('wishlist-product-card');
          // Also check for empty wishlist state
          var isEmpty = document.querySelector('.wk-wishlist-empty, [data-wk-empty]');

          if (hasCards) {
            clearInterval(wishlistCheckInterval);
            setTimeout(syncWithWishlistPage, 300);
          } else if (isEmpty) {
            // Empty wishlist - clear localStorage
            clearInterval(wishlistCheckInterval);
            saveItems([]);
            updateWishlistCount(0);
            console.log('[Gulcan Wishlist] Synced: wishlist is empty');
          } else if (wishlistCheckCount > 30) {
            // Timeout after 6 seconds - wishlist didn't load
            clearInterval(wishlistCheckInterval);
          }
        }, 200);
        return; // Don't run regular sync on wishlist page
      }

      // Then sync with page when buttons load (if any)
      var checkCount = 0;
      var checkInterval = setInterval(function() {
        checkCount++;
        // Check for any Swish button type
        var hasButtons = document.querySelector('wishlist-button-collection, wishlist-button, wk-button');
        if (hasButtons) {
          clearInterval(checkInterval);
          setTimeout(syncWithPage, 500);
        } else if (checkCount > 25) {
          clearInterval(checkInterval);
        }
      }, 200);
    }

    // Start when DOM is ready
    if (document.readyState === 'complete') {
      init();
    } else {
      window.addEventListener('load', init);
    }
  }

  // ==============================================================================
  // EVENT LISTENERS
  // ==============================================================================

  function initEventListeners() {
    // Menu toggle
    if (elements.menuTrigger) {
      elements.menuTrigger.addEventListener('click', toggleMenu);
    }

    // Menu close button
    if (elements.menuClose) {
      elements.menuClose.addEventListener('click', closeMenu);
    }

    // Menu overlay click
    if (elements.menuOverlay) {
      elements.menuOverlay.addEventListener('click', closeMenu);
    }

    // Search toggle
    if (elements.searchTrigger) {
      elements.searchTrigger.addEventListener('click', toggleSearch);
    }

    // Search close
    if (elements.searchClose) {
      elements.searchClose.addEventListener('click', closeSearch);
    }

    // Cart drawer trigger - opens cart drawer instead of going to /cart page
    if (elements.cartTrigger) {
      elements.cartTrigger.addEventListener('click', function(e) {
        e.preventDefault();
        // Use cart drawer API if available
        if (window.GulcanCartDrawer && typeof window.GulcanCartDrawer.open === 'function') {
          window.GulcanCartDrawer.open();
        } else {
          // Fallback to cart page
          window.location.href = '/cart';
        }
      });
    }

    // Submenu triggers (categories with children)
    var submenuTriggers = document.querySelectorAll('.gulcan-menu-has-children');
    submenuTriggers.forEach(function(trigger) {
      trigger.addEventListener('click', function() {
        var submenuId = this.getAttribute('data-submenu');
        openSubmenu(submenuId);
      });
    });

    // Back buttons in submenus
    var backButtons = document.querySelectorAll('[data-back-button]');
    backButtons.forEach(function(button) {
      button.addEventListener('click', closeSubmenu);
    });

    // Escape key to close menu/search
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape') {
        if (state.searchOpen) {
          closeSearch();
        }
        if (state.menuOpen) {
          if (state.currentSubmenu) {
            closeSubmenu();
          } else {
            closeMenu();
          }
        }
      }
    });

    // Listen for cart updates (Shopify cart events)
    document.addEventListener('cart:updated', function(event) {
      if (event.detail && typeof event.detail.item_count !== 'undefined') {
        updateCartCount(event.detail.item_count);
      } else {
        refreshCartCount();
      }
    });

    // Also listen for Shopify's section rendering events
    document.addEventListener('shopify:section:load', function() {
      refreshCartCount();
    });
  }

  // ==============================================================================
  // STICKY HEADER WITH ANNOUNCEMENT BAR SUPPORT
  // ==============================================================================

  /**
   * Handles sticky header behavior when there's an announcement bar above.
   * Header starts as position: relative, then switches to position: fixed
   * only AFTER the announcement bar has scrolled out of view.
   */
  function initStickyHeaderHandler() {
    var header = document.getElementById('gulcan-header');
    if (!header) return;

    // Only apply to sticky headers — skip when overlay is active (overlay has its own handler)
    if (!header.classList.contains('gulcan-header--sticky')) return;
    if (header.classList.contains('gulcan-header--overlay')) return;

    // Create placeholder element to prevent content jump when header becomes fixed
    var placeholder = document.createElement('div');
    placeholder.className = 'gulcan-header-placeholder';
    placeholder.style.height = header.offsetHeight + 'px';
    header.parentNode.insertBefore(placeholder, header.nextSibling);

    // Calculate the trigger point (header's original position from top of page)
    var headerOriginalTop = header.offsetTop;
    var ticking = false;

    function updateStickyHeader() {
      var scrollY = window.scrollY;

      // Recalculate header position if not stuck (in case page content changed)
      if (!header.classList.contains('is-stuck')) {
        headerOriginalTop = header.offsetTop;
      }

      // When scrolled past the header's original position, make it fixed
      if (scrollY >= headerOriginalTop) {
        if (!header.classList.contains('is-stuck')) {
          header.classList.add('is-stuck');
          placeholder.classList.add('is-active');
          placeholder.style.height = header.offsetHeight + 'px';
        }
      } else {
        if (header.classList.contains('is-stuck')) {
          header.classList.remove('is-stuck');
          placeholder.classList.remove('is-active');
        }
      }

      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(updateStickyHeader);
        ticking = true;
      }
    }, { passive: true });

    // Update placeholder height on resize
    window.addEventListener('resize', function() {
      placeholder.style.height = header.offsetHeight + 'px';
    }, { passive: true });

    // Initial check
    updateStickyHeader();

    // Watch for changes to elements above header (like promo bar dismissal)
    var promoBar = document.getElementById('gulcan-promo-bar');
    if (promoBar) {
      var observer = new MutationObserver(function() {
        // Recalculate header position after promo bar changes
        setTimeout(function() {
          headerOriginalTop = header.classList.contains('is-stuck')
            ? placeholder.offsetTop
            : header.offsetTop;
          updateStickyHeader();
        }, 100);
      });
      observer.observe(promoBar, { attributes: true, attributeFilter: ['style'] });
    }
  }

  // ==============================================================================
  // OVERLAY HEADER SCROLL HANDLING
  // ==============================================================================

  /**
   * Handles scroll behavior for overlay headers.
   * When scrolled past the header's original position, makes it fixed at top.
   * Header naturally flows after promo bar due to position: relative.
   */
  function initOverlayScrollHandler() {
    var header = document.getElementById('gulcan-header');
    if (!header) return;

    // Only apply to overlay headers
    if (!header.classList.contains('gulcan-header--overlay')) return;

    // Collection pages use overlay colors but should NOT pull content behind header
    var isCollectionPage = document.body.classList.contains('template-collection');

    // Find the element that is a sibling of <main> — this is what needs the negative margin
    var main = document.getElementById('MainContent');
    var headerSection = header.closest('.shopify-section');
    var headerGroup = headerSection;
    if (main) {
      var el = headerSection;
      while (el && el.parentElement) {
        if ([...el.parentElement.children].indexOf(main) !== -1) {
          headerGroup = el;
          break;
        }
        el = el.parentElement;
      }
    }
    var desktopNav = document.getElementById('gulcan-desktop-nav');

    // Total height = header + desktop nav (nav is 0 on mobile since display:none)
    var headerHeight = header.offsetHeight;
    var navHeight = desktopNav ? desktopNav.offsetHeight : 0;
    var totalOverlayHeight = headerHeight + navHeight;

    // Create placeholder if it doesn't exist
    var placeholder = document.getElementById('gulcan-header-placeholder');
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.id = 'gulcan-header-placeholder';
      placeholder.className = 'gulcan-header-placeholder';
      header.parentNode.insertBefore(placeholder, header.nextSibling);
    }

    // Pull main content up behind entire transparent header + desktop nav
    if (headerGroup && !isCollectionPage) {
      headerGroup.style.marginBottom = '-' + totalOverlayHeight + 'px';
      headerGroup.style.position = 'relative';
      headerGroup.style.zIndex = '999';
    }

    // Hero needs extra height + top padding to account for overlay eating into it
    if (main && !isCollectionPage) {
      // Set CSS variable so hero slide min-height includes overlay
      var heroSection = main.querySelector('[class*="gulcan-hero"]');
      if (heroSection) {
        heroSection.style.setProperty('--overlay-height', totalOverlayHeight + 'px');
      }
      // Push hero content below the overlay
      var heroContent = main.querySelector('.gulcan-hero__content');
      if (heroContent) {
        heroContent.style.paddingTop = (parseInt(getComputedStyle(heroContent).paddingTop) || 0) + totalOverlayHeight + 'px';
      }
    }

    var headerTop = header.getBoundingClientRect().top + window.scrollY;
    var ticking = false;

    function updateHeaderOnScroll() {
      var scrollY = window.scrollY;

      if (scrollY > headerTop) {
        if (!header.classList.contains('is-stuck')) {
          header.classList.add('is-stuck');
          // Reset negative margin when stuck — placeholder reserves the space
          if (headerGroup && !isCollectionPage) {
            headerGroup.style.marginBottom = '0px';
          }
          placeholder.style.height = totalOverlayHeight + 'px';
          placeholder.classList.add('is-active');
        }
      } else if (scrollY < 10) {
        if (header.classList.contains('is-stuck')) {
          header.classList.remove('is-stuck');
          // Restore negative margin for transparent overlay
          if (headerGroup && !isCollectionPage) {
            headerGroup.style.marginBottom = '-' + totalOverlayHeight + 'px';
          }
          placeholder.classList.remove('is-active');
        }
      }
      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(updateHeaderOnScroll);
        ticking = true;
      }
    }, { passive: true });

    // Initial check
    updateHeaderOnScroll();

    // Recalculate dimensions on resize
    window.addEventListener('resize', function() {
      headerHeight = header.offsetHeight;
      navHeight = desktopNav ? desktopNav.offsetHeight : 0;
      totalOverlayHeight = headerHeight + navHeight;
      headerTop = header.getBoundingClientRect().top + window.scrollY;
      if (headerGroup && !isCollectionPage && !header.classList.contains('is-stuck')) {
        headerGroup.style.marginBottom = '-' + totalOverlayHeight + 'px';
      }
    });
  }

  // ==============================================================================
  // INITIALIZATION
  // ==============================================================================

  function init() {
    // Initialize event listeners
    initEventListeners();

    // Initialize sticky header handler (for announcement bar offset)
    initStickyHeaderHandler();

    // Initialize overlay scroll handler
    initOverlayScrollHandler();

    // Initial cart count (page might have updated since HTML was rendered)
    // Only refresh if there's a cart on page (avoid unnecessary requests)
    // refreshCartCount(); // Commented out to avoid extra request on load

    // Initialize wishlist count
    refreshWishlistCount();
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ==============================================================================
  // WISHLIST KING FLOATING BUTTON FIX
  // ==============================================================================
  // WK injects floating wk-button elements inside wishlist-button-collection
  // but the floating buttons don't inherit the data-product-handle attribute.
  // This fix copies the handle from parent to child floating buttons.

  function fixWKFloatingButtons() {
    document.querySelectorAll('wk-button.wk-floating').forEach(function(btn) {
      if (!btn.getAttribute('data-product-handle')) {
        // Try to get handle from parent wishlist-button-collection or product card
        var parent = btn.closest('[data-product-handle]');
        if (parent) {
          btn.setAttribute('data-product-handle', parent.getAttribute('data-product-handle'));
        }
      }
    });
  }

  // Run fix on load and observe for dynamically added buttons
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixWKFloatingButtons);
  } else {
    fixWKFloatingButtons();
  }

  // Also run periodically to catch buttons added by WK after page load
  var wkFixAttempts = 0;
  var wkFixInterval = setInterval(function() {
    fixWKFloatingButtons();
    wkFixAttempts++;
    if (wkFixAttempts > 10) {
      clearInterval(wkFixInterval);
    }
  }, 500);

  // Observe for dynamically added WK buttons
  var wkObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        setTimeout(fixWKFloatingButtons, 100);
      }
    });
  });
  wkObserver.observe(document.body, { childList: true, subtree: true });

  // ==============================================================================
  // PUBLIC API (for external integrations)
  // ==============================================================================

  /**
   * Opens the cart drawer (delegates to GulcanCartDrawer)
   */
  function openCartDrawer() {
    if (window.GulcanCartDrawer && typeof window.GulcanCartDrawer.open === 'function') {
      window.GulcanCartDrawer.open();
    }
  }

  window.GulcanHeader = {
    openMenu: openMenu,
    closeMenu: closeMenu,
    toggleMenu: toggleMenu,
    openSearch: openSearch,
    closeSearch: closeSearch,
    toggleSearch: toggleSearch,
    openCartDrawer: openCartDrawer,
    updateCartCount: updateCartCount,
    updateWishlistCount: updateWishlistCount,
    refreshCartCount: refreshCartCount
  };

})();

// ==============================================================================
// COLLECTION GRID - MOVE QUICK-ADD BUTTONS INTO IMAGE WRAPPER
// ==============================================================================
// Repositions quick-add buttons to overlay on product images (like gulcan-atc-btn)
// Only runs on collection/search pages with grid products
(function() {
  'use strict';

  function moveQuickAddButtons() {
    var products = document.querySelectorAll('.grid-product');

    products.forEach(function(product) {
      // Use image-mask (actual image container), not image-wrapper (includes text)
      var imageMask = product.querySelector('.grid-product__image-mask');
      var quickAdd = product.querySelector('.grid-product__quick-add');

      // Only move if both exist and quick-add is not already in image mask
      if (imageMask && quickAdd && !imageMask.contains(quickAdd)) {
        // Move the quick-add element into the image mask (on top of image)
        imageMask.appendChild(quickAdd);
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', moveQuickAddButtons);
  } else {
    moveQuickAddButtons();
  }

  // Also run after AJAX loads (for infinite scroll, filtering)
  document.addEventListener('ajaxinate:loaded', moveQuickAddButtons);

  // Observe for dynamically added products
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length > 0) {
        // Debounce to avoid running too often
        clearTimeout(window.quickAddMoveTimeout);
        window.quickAddMoveTimeout = setTimeout(moveQuickAddButtons, 100);
      }
    });
  });

  // Observe collection grids
  var productGrid = document.querySelector('.collection-grid, #CollectionProductGrid, .grid--collection');
  if (productGrid) {
    observer.observe(productGrid, { childList: true, subtree: true });
  }

  // Also observe product recommendations (loads via AJAX on PDP)
  var productRecs = document.querySelectorAll('product-recommendations');
  productRecs.forEach(function(rec) {
    observer.observe(rec, { childList: true, subtree: true });
  });

  // Observe main content for any dynamically added grids
  var mainContent = document.querySelector('main, #MainContent, .main-content');
  if (mainContent) {
    var mainObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // Check if it's a product-recommendations or contains grid-products
            if (node.tagName === 'PRODUCT-RECOMMENDATIONS' ||
                node.querySelector && node.querySelector('.grid-product')) {
              clearTimeout(window.quickAddMoveTimeout);
              window.quickAddMoveTimeout = setTimeout(moveQuickAddButtons, 100);
            }
          }
        });
      });
    });
    mainObserver.observe(mainContent, { childList: true, subtree: true });
  }
})();

// ==============================================================================
// SOLD OUT BUTTON CLICK HANDLER
// ==============================================================================
// Makes sold-out buttons clickable and triggers REZ back-in-stock notification
(function() {
  'use strict';

  // Remove disabled attribute from sold-out buttons to make them clickable
  function enableSoldOutButtons() {
    var soldOutBtns = document.querySelectorAll('.quick-add-btn--sold-out[disabled]');
    soldOutBtns.forEach(function(btn) {
      btn.removeAttribute('disabled');
      btn.setAttribute('data-sold-out', 'true'); // Mark as sold out for styling
    });
  }

  // Run on load and observe for dynamically added buttons
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableSoldOutButtons);
  } else {
    enableSoldOutButtons();
  }

  // Also run after AJAX loads
  document.addEventListener('ajaxinate:loaded', enableSoldOutButtons);

  // Observe for dynamically added products
  var soldOutObserver = new MutationObserver(function(mutations) {
    clearTimeout(window.soldOutEnableTimeout);
    window.soldOutEnableTimeout = setTimeout(enableSoldOutButtons, 100);
  });

  var productGrid = document.querySelector('.collection-grid, #CollectionProductGrid, .grid--collection, main');
  if (productGrid) {
    soldOutObserver.observe(productGrid, { childList: true, subtree: true });
  }

  // Handle clicks on sold-out buttons in product grid
  document.addEventListener('click', function(e) {
    var soldOutBtn = e.target.closest('.quick-add-btn--sold-out, [data-sold-out="true"]');
    if (!soldOutBtn) return;

    e.preventDefault();
    e.stopPropagation();

    // Find the parent product card and get the product link
    var productCard = soldOutBtn.closest('.grid-product');
    if (!productCard) return;

    var productLink = productCard.querySelector('.grid-product__link');
    var productUrl = '';

    if (productLink && productLink.href) {
      productUrl = productLink.href;
    } else {
      // Fallback: try to get from data attribute
      var productHandle = productCard.dataset.productHandle;
      if (productHandle) {
        productUrl = '/products/' + productHandle;
      }
    }

    if (productUrl) {
      // Add hash to trigger REZ modal on PDP load
      window.location.href = productUrl + '#notify-me';
    }
  });

  // On PDP: Auto-click REZ button if #notify-me hash is present
  if (window.location.hash === '#notify-me') {
    // Wait for REZ app to load
    var checkForRezButton = function() {
      var rezBtn = document.querySelector('.rez-stock-notification-button');
      if (rezBtn) {
        // Scroll to the button area first
        rezBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Click the REZ button after a short delay
        setTimeout(function() {
          rezBtn.click();
          // Remove hash from URL
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }, 500);
      } else {
        // REZ button not found yet, retry
        setTimeout(checkForRezButton, 200);
      }
    };

    // Start checking after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(checkForRezButton, 500);
      });
    } else {
      setTimeout(checkForRezButton, 500);
    }
  }
})();

// ==============================================================================
// COLLECTION PAGE - REORDER ELEMENTS
// ==============================================================================
// Reorders collection page elements for better UX
// Desired order: Title → Description → Categories → Products
(function() {
  'use strict';

  function reorderCollectionPage() {
    // Only run on collection pages
    if (!document.body.classList.contains('template-collection')) return;

    var description = document.querySelector('.collection__description');
    var categoriesSection = document.querySelector('.gulcan-collection-categories-section');

    // If no description, nothing to reorder around
    if (!description) return;

    // 1. Add collection title if not already present
    var existingTitle = document.querySelector('.gulcan-collection-title');
    if (!existingTitle) {
      // Get title from page title (format: "Collection Name – Store Name")
      var pageTitle = document.title.split(' – ')[0].split(' | ')[0].trim();

      if (pageTitle) {
        var titleEl = document.createElement('h1');
        titleEl.className = 'gulcan-collection-title';
        titleEl.textContent = pageTitle;
        // Insert before description
        description.parentNode.insertBefore(titleEl, description);
      }
    }

    // 2. Move categories carousel after description (before products)
    if (categoriesSection && !description.parentNode.contains(categoriesSection)) {
      // Insert after description
      if (description.nextSibling) {
        description.parentNode.insertBefore(categoriesSection, description.nextSibling);
      } else {
        description.parentNode.appendChild(categoriesSection);
      }
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reorderCollectionPage);
  } else {
    reorderCollectionPage();
  }
})();

// ==============================================================================
// SEARCH DRAWER - PREDICTIVE SEARCH
// ==============================================================================
// Full-featured search drawer with:
// - Predictive search via Shopify API
// - Recent searches stored in localStorage
// - Product recommendations
(function() {
  'use strict';

  var STORAGE_KEY = 'gulcan_recent_searches';
  var MAX_RECENT_SEARCHES = 5;
  var DEBOUNCE_DELAY = 300;
  var MIN_QUERY_LENGTH = 2;

  // DOM Elements
  var drawer = document.getElementById('gulcan-search-drawer');
  var overlay = document.getElementById('gulcan-search-overlay');
  var closeBtn = document.getElementById('gulcan-search-drawer-close');
  var input = document.getElementById('gulcan-search-drawer-input');
  var clearBtn = document.getElementById('gulcan-search-clear');
  var form = document.getElementById('gulcan-search-form');

  // Sections
  var recentSection = document.getElementById('gulcan-search-recent');
  var recentList = document.getElementById('gulcan-search-recent-list');
  var clearRecentBtn = document.getElementById('gulcan-search-clear-recent');
  var categoriesSection = document.getElementById('gulcan-search-categories');
  var resultsSection = document.getElementById('gulcan-search-results');
  var resultsGrid = document.getElementById('gulcan-search-results-grid');
  var viewAllLink = document.getElementById('gulcan-search-view-all');
  var noResultsSection = document.getElementById('gulcan-search-no-results');
  var queryText = document.getElementById('gulcan-search-query-text');
  var loadingSection = document.getElementById('gulcan-search-loading');
  var recommendationsSection = document.getElementById('gulcan-search-recommendations');

  // State
  var isOpen = false;
  var debounceTimer = null;
  var currentQuery = '';

  // ==============================================================================
  // DRAWER OPEN/CLOSE
  // ==============================================================================

  function openDrawer() {
    if (isOpen || !drawer) return;
    isOpen = true;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gulcan-search-open');

    // Show recent searches or recommendations
    showInitialState();

    // Focus input
    setTimeout(function() {
      if (input) input.focus();
    }, 100);
  }

  function closeDrawer() {
    if (!isOpen || !drawer) return;
    isOpen = false;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gulcan-search-open');

    // Clear input
    if (input) input.value = '';
    if (clearBtn) clearBtn.style.display = 'none';
    currentQuery = '';
  }

  // ==============================================================================
  // RECENT SEARCHES (localStorage)
  // ==============================================================================

  function getRecentSearches() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecentSearch(query) {
    if (!query || query.length < MIN_QUERY_LENGTH) return;

    var searches = getRecentSearches();
    // Remove if already exists
    searches = searches.filter(function(s) {
      return s.toLowerCase() !== query.toLowerCase();
    });
    // Add to front
    searches.unshift(query);
    // Limit to max
    searches = searches.slice(0, MAX_RECENT_SEARCHES);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
    } catch (e) {}
  }

  function removeRecentSearch(query) {
    var searches = getRecentSearches();
    searches = searches.filter(function(s) {
      return s.toLowerCase() !== query.toLowerCase();
    });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
    } catch (e) {}
    renderRecentSearches();
  }

  function clearAllRecentSearches() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    renderRecentSearches();
  }

  function renderRecentSearches() {
    var searches = getRecentSearches();

    if (!recentSection || !recentList) return;

    if (searches.length === 0) {
      recentSection.style.display = 'none';
      return;
    }

    recentSection.style.display = '';
    recentList.innerHTML = searches.map(function(query) {
      return '<li class="gulcan-search-drawer__recent-item" data-search-query="' + escapeHtml(query) + '">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>' +
        '<span>' + escapeHtml(query) + '</span>' +
        '<button type="button" class="gulcan-search-drawer__recent-remove" data-remove-query="' + escapeHtml(query) + '" aria-label="Verwijderen">' +
        '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
        '</button></li>';
    }).join('');
  }

  // ==============================================================================
  // PREDICTIVE SEARCH
  // ==============================================================================

  function performSearch(query) {
    if (!query || query.length < MIN_QUERY_LENGTH) {
      showInitialState();
      return;
    }

    currentQuery = query;
    showLoading();

    // Predictive search default fields don't include tags, so brand searches
    // like "bricard bondy" miss when the brand only lives in product tags.
    // Explicitly include tag in the searched fields so brand/category tags
    // match alongside the defaults (title, vendor, product_type, etc.).
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=hide' +
      '&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,body,tag';

    fetch(url)
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        // Check if query is still current (user might have typed more)
        if (query !== currentQuery) return;

        var products = data.resources && data.resources.results && data.resources.results.products;
        if (products && products.length > 0) {
          showResults(products, query);
        } else {
          showNoResults(query);
        }
      })
      .catch(function(error) {
        console.warn('Gulcan Search: Error fetching results', error);
        showNoResults(query);
      });
  }

  function showLoading() {
    if (recentSection) recentSection.style.display = 'none';
    if (categoriesSection) categoriesSection.style.display = 'none';
    if (resultsSection) resultsSection.style.display = 'none';
    if (noResultsSection) noResultsSection.style.display = 'none';
    if (recommendationsSection) recommendationsSection.style.display = 'none';
    if (loadingSection) loadingSection.style.display = '';
  }

  function showResults(products, query) {
    if (loadingSection) loadingSection.style.display = 'none';
    if (recentSection) recentSection.style.display = 'none';
    if (categoriesSection) categoriesSection.style.display = 'none';
    if (noResultsSection) noResultsSection.style.display = 'none';
    if (recommendationsSection) recommendationsSection.style.display = 'none';
    if (resultsSection) resultsSection.style.display = '';

    if (resultsGrid) {
      resultsGrid.innerHTML = products.map(function(product) {
        var price = formatMoney(product.price);
        var comparePrice = product.compare_at_price_max > product.price ? formatMoney(product.compare_at_price_max) : '';
        var swatchesHtml = renderSearchColorSwatches(product);

        return '<a href="' + product.url + '" class="gulcan-search-drawer__product" data-product-id="' + product.id + '">' +
          '<div class="gulcan-search-drawer__product-image">' +
          '<img src="' + resizeImage(product.image, '200x') + '" alt="' + escapeHtml(product.title) + '" width="80" height="80" loading="lazy">' +
          '</div>' +
          '<div class="gulcan-search-drawer__product-info">' +
          '<p class="gulcan-search-drawer__product-title">' + escapeHtml(product.title) + '</p>' +
          '<p class="gulcan-search-drawer__product-price">' + price +
          (comparePrice ? '<span class="gulcan-search-drawer__product-compare">' + comparePrice + '</span>' : '') +
          '</p>' +
          swatchesHtml +
          '</div></a>';
      }).join('');
    }

    if (viewAllLink) {
      viewAllLink.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
    }

    // After the basic results are in the DOM, fetch each product.json to get
    // option/variant info and inject color swatches. Predictive search alone
    // doesn't return options, so we top up with the storefront product feed.
    populateSearchResultSwatches(products);
  }

  // Renders an empty container that the swatch loader fills in async. We
  // include the container so layout doesn't shift when swatches arrive.
  function renderSearchColorSwatches(product) {
    return '<div class="gulcan-search-drawer__swatches" data-search-swatches data-product-handle="' +
      escapeHtml(getHandleFromUrl(product.url)) + '"></div>';
  }

  // Cache product.json responses across queries so re-running a search
  // doesn't refetch.
  var searchProductCache = {};

  function populateSearchResultSwatches(products) {
    products.forEach(function(product) {
      var handle = getHandleFromUrl(product.url);
      if (!handle) return;

      var renderInto = function(p) {
        var slot = document.querySelector(
          '.gulcan-search-drawer__swatches[data-product-handle="' +
          cssEscape(handle) + '"]'
        );
        if (!slot) return;

        // /products/{handle}.json returns options as objects like
        // { name: "Color", position: 1, values: [...] }, not strings.
        // And each variant has option1/option2/option3 (not an options array).
        var colorIdx = -1;
        (p.options || []).forEach(function(opt, i) {
          var optName = typeof opt === 'string' ? opt : (opt && opt.name) || '';
          var lower = optName.toLowerCase();
          if (lower === 'color' || lower === 'colour' || lower === 'kleur') colorIdx = i;
        });
        if (colorIdx === -1) return;

        // Resolve a variant's image. /products/{handle}.json usually leaves
        // variant.featured_image undefined but populates variant.image_id, so
        // we look that up against product.images.
        var resolveVariantImage = function(v) {
          if (v.featured_image) {
            if (typeof v.featured_image === 'string') return { src: v.featured_image };
            if (v.featured_image.src) return v.featured_image;
          }
          if (v.image_id && p.images) {
            var match = p.images.find(function(img) { return img && img.id === v.image_id; });
            if (match) return typeof match === 'string' ? { src: match } : match;
          }
          return null;
        };

        // Collect unique color values (max 6 to avoid overflow).
        // product.json doesn't include `available`; treat undefined as available.
        var seen = {};
        var values = [];
        (p.variants || []).forEach(function(v) {
          var val = v['option' + (colorIdx + 1)];
          if (val && !seen[val]) {
            seen[val] = { value: val, image: resolveVariantImage(v), available: v.available !== false };
            values.push(seen[val]);
          }
        });

        if (values.length < 2) return; // Don't show single-color row

        // Find the variant that matches each color value so the swatch can
        // carry the variant id, image and price for click-to-switch.
        var variantByValue = {};
        (p.variants || []).forEach(function(v) {
          var val = v['option' + (colorIdx + 1)];
          if (val && !variantByValue[val]) variantByValue[val] = v;
        });

        // First swatch is active by default (matches the first product image).
        var html = values.slice(0, 6).map(function(item, i) {
          var bg = colorNameToHex(item.value);
          var imgSrc = item.image && item.image.src ? item.image.src : '';
          var border = bg === '#ffffff' ? ' border:1px solid #d0d0d0;' : '';
          var variant = variantByValue[item.value];
          var attrs = ' role="button" tabindex="0"' +
            ' data-search-swatch-btn' +
            ' data-color-value="' + escapeHtml(item.value) + '"' +
            (variant ? ' data-variant-id="' + variant.id + '"' : '') +
            (variant ? ' data-variant-price="' + variant.price + '"' : '') +
            (variant && variant.compare_at_price ? ' data-variant-compare="' + variant.compare_at_price + '"' : '') +
            (imgSrc ? ' data-variant-image="' + escapeHtml(imgSrc) + '"' : '');
          var activeCls = i === 0 ? ' is-active' : '';
          if (imgSrc) {
            return '<span class="gulcan-search-drawer__swatch' + activeCls + '"' + attrs + ' title="' + escapeHtml(item.value) + '">' +
              '<img src="' + imgSrc.replace(/(\.[^.\/?]+)(\?|$)/, '_60x60$1$2') + '" alt="' + escapeHtml(item.value) + '">' +
              '</span>';
          }
          return '<span class="gulcan-search-drawer__swatch' + activeCls + '"' + attrs +
            ' title="' + escapeHtml(item.value) +
            '" style="background-color:' + bg + ';' + border + '"></span>';
        }).join('');

        if (values.length > 6) {
          html += '<span class="gulcan-search-drawer__swatch-more">+' + (values.length - 6) + '</span>';
        }

        slot.innerHTML = html;
      };

      if (searchProductCache[handle]) {
        renderInto(searchProductCache[handle]);
        return;
      }

      fetch('/products/' + handle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          searchProductCache[handle] = data.product;
          renderInto(data.product);
        })
        .catch(function() { /* silent */ });
    });
  }

  // Click on a search-drawer swatch: swap the card's image, price, and the
  // variant on the parent product link so click-through opens the right SKU.
  // Capture phase + stopImmediatePropagation so the click on the swatch
  // does NOT bubble up to the parent <a> and navigate to the PDP.
  function initSearchDrawerSwatchClicks() {
    document.addEventListener('click', function(e) {
      var swatch = e.target.closest('[data-search-swatch-btn]');
      if (!swatch) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      var card = swatch.closest('.gulcan-search-drawer__product');
      if (!card) return;

      // Toggle active state within this card's swatch row only.
      var row = swatch.parentElement;
      if (row) {
        Array.prototype.forEach.call(row.querySelectorAll('[data-search-swatch-btn]'), function(s) {
          s.classList.remove('is-active');
        });
      }
      swatch.classList.add('is-active');

      // Update card image
      var newImg = swatch.dataset.variantImage;
      if (newImg) {
        var imgEl = card.querySelector('.gulcan-search-drawer__product-image img');
        if (imgEl) {
          imgEl.src = newImg.replace(/(\.[^.\/?]+)(\?|$)/, '_400x$1$2');
          if (imgEl.srcset) imgEl.srcset = '';
        }
      }

      // Update price (current + compare). Variant prices come from product.json
      // as decimal strings ("12.95") - format them in cents so formatMoney works.
      var priceEl = card.querySelector('.gulcan-search-drawer__product-price');
      if (priceEl) {
        var price = parseFloat(swatch.dataset.variantPrice);
        var compare = swatch.dataset.variantCompare ? parseFloat(swatch.dataset.variantCompare) : null;
        if (!isNaN(price)) {
          var cents = Math.round(price * 100);
          var compareCents = compare && !isNaN(compare) ? Math.round(compare * 100) : null;
          var html = formatMoney(cents);
          if (compareCents && compareCents > cents) {
            html += '<span class="gulcan-search-drawer__product-compare">' + formatMoney(compareCents) + '</span>';
          }
          priceEl.innerHTML = html;
        }
      }

      // Update the card link's variant param so clicking opens the right SKU
      var variantId = swatch.dataset.variantId;
      if (variantId) {
        var href = card.getAttribute('href') || '';
        href = href.replace(/[?&]variant=\d+/g, '');
        href = href.replace(/\?&/, '?').replace(/\?$/, '');
        var sep = href.indexOf('?') > -1 ? '&' : '?';
        card.setAttribute('href', href + sep + 'variant=' + variantId);
      }
    }, true); // capture
  }

  function getHandleFromUrl(url) {
    if (!url) return '';
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  // Minimal CSS attribute selector escape (handle handles only contain
  // a-z, 0-9, hyphens normally, but be safe).
  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  // Same color map as the PDP/card so swatches look identical everywhere.
  function colorNameToHex(value) {
    if (!value) return '#cccccc';
    var key = value.toLowerCase().trim();
    var map = {
      'zwart': '#000000', 'black': '#000000',
      'wit': '#ffffff', 'white': '#ffffff',
      'rood': '#e74c3c', 'red': '#e74c3c',
      'blauw': '#3498db', 'blue': '#3498db',
      'groen': '#27ae60', 'green': '#27ae60',
      'geel': '#f1c40f', 'yellow': '#f1c40f',
      'oranje': '#e67e22', 'orange': '#e67e22',
      'paars': '#9b59b6', 'purple': '#9b59b6',
      'roze': '#e91e63', 'pink': '#e91e63',
      'bruin': '#795548', 'brown': '#795548',
      'grijs': '#95a5a6', 'grey': '#95a5a6', 'gray': '#95a5a6',
      'goud': '#d4af37', 'gold': '#d4af37',
      'zilver': '#c0c0c0', 'silver': '#c0c0c0',
      'beige': '#f5f5dc',
      'creme': '#fffdd0', 'cream': '#fffdd0',
      'navy': '#001f3f', 'marineblauw': '#001f3f',
      'bordeaux': '#800020', 'burgundy': '#800020',
      'turquoise': '#40e0d0', 'turkoois': '#40e0d0',
      'koper': '#b87333', 'copper': '#b87333',
      'antraciet': '#293133', 'anthracite': '#293133'
    };
    return map[key] || '#cccccc';
  }

  function showNoResults(query) {
    if (loadingSection) loadingSection.style.display = 'none';
    if (recentSection) recentSection.style.display = 'none';
    if (categoriesSection) categoriesSection.style.display = 'none';
    if (resultsSection) resultsSection.style.display = 'none';
    if (noResultsSection) noResultsSection.style.display = '';
    if (recommendationsSection) recommendationsSection.style.display = '';

    if (queryText) queryText.textContent = query;
  }

  function showInitialState() {
    if (loadingSection) loadingSection.style.display = 'none';
    if (resultsSection) resultsSection.style.display = 'none';
    if (noResultsSection) noResultsSection.style.display = 'none';
    if (categoriesSection) categoriesSection.style.display = '';
    if (recommendationsSection) recommendationsSection.style.display = '';

    renderRecentSearches();
  }

  // ==============================================================================
  // HELPERS
  // ==============================================================================

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = (parseInt(cents, 10) / 100).toFixed(2);
    return '€' + value.replace('.', ',');
  }

  function resizeImage(url, size) {
    if (!url) return '';
    // Shopify image URL transformation
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_' + size + '.$1$2');
  }

  // ==============================================================================
  // EVENT LISTENERS
  // ==============================================================================

  function init() {
    if (!drawer) return;

    // Open drawer when clicking search trigger
    var searchTrigger = document.getElementById('gulcan-search-trigger');
    if (searchTrigger) {
      searchTrigger.addEventListener('click', function(e) {
        e.preventDefault();
        openDrawer();
      });
    }

    // Wire up swatch click-to-switch for search results (delegated on document)
    initSearchDrawerSwatchClicks();

    // Close drawer
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) {
        closeDrawer();
      }
    });

    // Input handling
    if (input) {
      input.addEventListener('input', function() {
        var query = this.value.trim();

        // Show/hide clear button
        if (clearBtn) {
          clearBtn.style.display = query.length > 0 ? '' : 'none';
        }

        // Debounce search
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function() {
          performSearch(query);
        }, DEBOUNCE_DELAY);
      });
    }

    // Clear input button
    if (clearBtn) {
      clearBtn.addEventListener('click', function() {
        if (input) {
          input.value = '';
          input.focus();
        }
        clearBtn.style.display = 'none';
        showInitialState();
      });
    }

    // Form submit - save to recent searches
    if (form) {
      form.addEventListener('submit', function() {
        var query = input ? input.value.trim() : '';
        if (query.length >= MIN_QUERY_LENGTH) {
          saveRecentSearch(query);
        }
      });
    }

    // Clear all recent searches
    if (clearRecentBtn) {
      clearRecentBtn.addEventListener('click', clearAllRecentSearches);
    }

    // Recent search click (delegate)
    if (recentList) {
      recentList.addEventListener('click', function(e) {
        // Remove button
        var removeBtn = e.target.closest('[data-remove-query]');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          removeRecentSearch(removeBtn.dataset.removeQuery);
          return;
        }

        // Search term click
        var item = e.target.closest('[data-search-query]');
        if (item) {
          var query = item.dataset.searchQuery;
          if (input) input.value = query;
          if (clearBtn) clearBtn.style.display = '';
          performSearch(query);
        }
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  window.GulcanSearchDrawer = {
    open: openDrawer,
    close: closeDrawer
  };

})();

// ==============================================================================
// RECENTLY VIEWED PRODUCTS - Track & Display
// ==============================================================================
(function() {
  'use strict';

  var STORAGE_KEY = 'gulcan_recently_viewed';
  var MAX_ITEMS = 12;

  // ==============================================================================
  // TRACK PRODUCT VIEW
  // ==============================================================================

  /**
   * Validates that a product has all required fields
   * @param {Object} product - Product data to validate
   * @returns {boolean} - True if product has all required fields
   */
  function isValidProduct(product) {
    return product &&
      product.id &&
      product.handle &&
      product.title &&
      product.url &&
      typeof product.price === 'number';
  }

  function trackProductView() {
    // Only run on product pages
    var productJson = document.querySelector('[data-product-json]');
    if (!productJson) return;

    try {
      var product = JSON.parse(productJson.textContent);

      // Validate required fields exist
      if (!product || !product.id || !product.handle || !product.title) return;

      // Validate price is a number
      if (typeof product.price !== 'number') return;

      var productData = {
        id: product.id,
        handle: product.handle,
        title: product.title,
        url: '/products/' + product.handle,
        price: product.price,
        compare_at_price: product.compare_at_price || null,
        image: product.featured_image || null,
        timestamp: Date.now()
      };

      saveViewedProduct(productData);
    } catch (e) {
      // Silently fail
    }
  }

  function saveViewedProduct(productData) {
    // Validate before saving
    if (!isValidProduct(productData)) return;

    var viewed = getViewedProducts();

    // Dedupe by handle (survives product re-imports; id does not).
    viewed = viewed.filter(function(p) {
      return p.handle !== productData.handle && p.id !== productData.id;
    });

    // Add to beginning
    viewed.unshift(productData);

    // Limit to max items
    if (viewed.length > MAX_ITEMS) {
      viewed = viewed.slice(0, MAX_ITEMS);
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(viewed));
    } catch (e) {
      // localStorage full or unavailable
    }
  }

  function getViewedProducts() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      var products = JSON.parse(stored);

      // Filter out invalid entries and collapse duplicates by handle
      // (re-imported products keep the same handle but get a new id).
      var seenHandles = {};
      var clean = [];
      products.forEach(function(p) {
        if (!isValidProduct(p)) return;
        if (p.handle && seenHandles[p.handle]) return;
        if (p.handle) seenHandles[p.handle] = true;
        clean.push(p);
      });
      return clean;
    } catch (e) {
      return [];
    }
  }

  // ==============================================================================
  // RENDER RECENTLY VIEWED CAROUSEL
  // ==============================================================================
  function renderRecentlyViewed() {
    var container = document.getElementById('gulcan-recently-viewed');
    if (!container) return;

    var track = container.querySelector('[data-carousel-track]');
    if (!track) return;

    var products = getViewedProducts();

    // Hide section if no products
    if (products.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Show section
    container.style.display = '';

    // Build HTML
    var html = products.map(function(product) {
      var imageUrl = product.image || '';
      // Transform to 300px width for display
      if (imageUrl && imageUrl.indexOf('cdn.shopify.com') > -1) {
        imageUrl = imageUrl.replace(/\?.*$/, '') + '?width=300';
      }

      var priceHtml = formatMoney(product.price);
      if (product.compare_at_price && product.compare_at_price > product.price) {
        priceHtml += ' <span class="gulcan-carousel__product-compare">' + formatMoney(product.compare_at_price) + '</span>';
      }

      var badgeHtml = '';
      if (product.compare_at_price && product.compare_at_price > product.price) {
        var discount = Math.round((product.compare_at_price - product.price) / product.compare_at_price * 100);
        badgeHtml = '<span class="gulcan-carousel__badge">-' + discount + '%</span>';
      }

      return '<a href="' + product.url + '" class="gulcan-carousel__product" data-product-id="' + product.id + '" data-product-handle="' + escapeHtml(product.handle || '') + '">' +
        '<div class="gulcan-carousel__product-image">' +
          badgeHtml +
          '<img src="' + imageUrl + '" alt="' + escapeHtml(product.title) + '" width="120" height="120" loading="lazy" data-recent-image>' +
        '</div>' +
        '<div class="gulcan-carousel__product-info">' +
          '<p class="gulcan-carousel__product-title">' + escapeHtml(truncate(product.title, 35)) + '</p>' +
          '<p class="gulcan-carousel__product-price">' + priceHtml + '</p>' +
        '</div>' +
      '</a>';
    }).join('');

    track.innerHTML = html;

    // Reset scroll position to start (left edge)
    track.scrollLeft = 0;

    // Stale-image recovery: when a stored image URL 404s (product re-imported),
    // re-fetch the current featured image from /products/:handle.js and update.
    track.querySelectorAll('[data-recent-image]').forEach(function(img) {
      img.addEventListener('error', function onErr() {
        img.removeEventListener('error', onErr);
        var tile = img.closest('[data-product-handle]');
        var handle = tile && tile.getAttribute('data-product-handle');
        if (!handle) {
          tile && tile.remove();
          return;
        }
        fetch('/products/' + handle + '.js')
          .then(function(r) { return r.ok ? r.json() : Promise.reject(r.status); })
          .then(function(p) {
            var fresh = p.featured_image || (p.images && p.images[0]) || '';
            if (!fresh) { tile.remove(); return; }
            if (fresh.indexOf('//') === 0) fresh = 'https:' + fresh;
            img.src = fresh.replace(/\?.*$/, '') + '?width=300';
            // Persist fix to localStorage so next load is clean.
            try {
              var list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
              list.forEach(function(entry) {
                if (entry.handle === handle) {
                  entry.image = fresh;
                  entry.id = p.id;
                }
              });
              localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
            } catch (e) {}
          })
          .catch(function() {
            tile.remove();
          });
      });
    });
  }

  // ==============================================================================
  // CAROUSEL NAVIGATION
  // ==============================================================================
  function initCarouselNavigation() {
    var carousels = document.querySelectorAll('.gulcan-search-drawer__carousel');

    carousels.forEach(function(carousel) {
      var track = carousel.querySelector('[data-carousel-track]');
      var prevBtn = carousel.querySelector('[data-carousel-prev]');
      var nextBtn = carousel.querySelector('[data-carousel-next]');

      if (!track) return;

      // Reset scroll position to start (left edge)
      track.scrollLeft = 0;

      // Calculate scroll amount (roughly 2 products)
      var scrollAmount = 380;

      if (prevBtn) {
        prevBtn.addEventListener('click', function() {
          track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function() {
          track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        });
      }

      // Update button states on scroll
      function updateButtonStates() {
        if (prevBtn) {
          prevBtn.disabled = track.scrollLeft <= 0;
        }
        if (nextBtn) {
          nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 10;
        }
      }

      track.addEventListener('scroll', updateButtonStates);
      updateButtonStates();
    });
  }

  // ==============================================================================
  // HELPERS
  // ==============================================================================
  function formatMoney(cents) {
    // Handle null, undefined, or invalid values
    if (cents === null || cents === undefined || cents === '') {
      return '€0,00';
    }
    // If it's a string with comma (already formatted), return as-is
    if (typeof cents === 'string' && cents.includes('€')) {
      return cents;
    }
    // Convert string to number
    if (typeof cents === 'string') {
      cents = parseFloat(cents.replace(',', '.').replace(/[^\d.]/g, ''));
    }
    // Handle NaN
    if (isNaN(cents)) {
      return '€0,00';
    }
    // Shopify returns cents as integers (e.g., 2495 for €24.95)
    var value = (cents / 100).toFixed(2);
    return '€' + value.replace('.', ',');
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function truncate(str, length) {
    if (!str) return '';
    if (str.length <= length) return str;
    return str.substring(0, length) + '...';
  }

  // ==============================================================================
  // INITIALIZATION
  // ==============================================================================
  function init() {
    // Track current product view (if on PDP)
    trackProductView();

    // Render recently viewed when search drawer opens
    document.addEventListener('click', function(e) {
      var trigger = e.target.closest('#gulcan-search-trigger');
      if (trigger) {
        // Small delay to ensure drawer is open
        setTimeout(function() {
          renderRecentlyViewed();
          initCarouselNavigation();
        }, 50);
      }
    });

    // Also render on page load if drawer is somehow open
    renderRecentlyViewed();
    initCarouselNavigation();
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  window.GulcanRecentlyViewed = {
    track: trackProductView,
    render: renderRecentlyViewed,
    get: getViewedProducts
  };

})();
